/* ============================================================================
 * EMBASSY › Charter — what the Immersion purchase confers
 * ----------------------------------------------------------------------------
 * Renders the franchise charter. Every right keeps its row and states its status,
 * because a charter that hides its ungranted rights is a brochure.
 *
 * Nothing here mints, signs or deploys. The collection is deployed, so the
 * franchise address is derivable and the panel shows it as derived, not live.
 * ========================================================================= */

import { useMemo } from 'react';
import { useWallet } from '../hooks/useWallet';
import {
  CUSTODY_MODES,
  charterSummary,
  buildCharter,
  type GrantStatus,
} from '../lib/charter';
import { FRANCHISE_ANCHOR, MATRIX, STATUS_TONE, matrixTotals } from '../lib/matrix';
import { termsIn } from '../lib/lexicon';
import { IMMERSION_TOKEN_CONTRACT } from '../lib/immersion';

const TONE: Record<GrantStatus, string> = {
  'on-purchase': 'badge badge-admin',
  proposed: 'badge badge-beta',
  'not-wired': 'badge badge-claude',
};

const TONE_WORD: Record<GrantStatus, string> = {
  'on-purchase': 'granted',
  proposed: 'proposed',
  'not-wired': 'not wired',
};

export function EmbassyCharter() {
  const w = useWallet();

  const charter = useMemo(
    () =>
      buildCharter({
        holderEns: w.ensName ?? null,
        tokenContract: import.meta.env.VITE_IMMERSION_TOKEN_CONTRACT || IMMERSION_TOKEN_CONTRACT,
      }),
    [w.ensName],
  );

  const namespaceTerms = charter.lexiconNamespace
    ? termsIn(charter.lexiconNamespace.split('.')[0])
    : [];

  return (
    <div className="dsp stack">
      {/* ── the charter itself ────────────────────────────────────────────── */}
      <div className="sheet">
        <div className="page__bar">
          <div className="grow">
            <p className="section-h">
              immersion charter <span className="dim">franchise</span>
            </p>
            <p className="cap">{charterSummary(charter)}</p>
          </div>
          <div className="row" style={{ gap: 6 }}>
            {charter.state === 'issued' ? (
              <span className="badge badge-admin">derived</span>
            ) : (
              <span className="badge badge-claude">unissued</span>
            )}
            <span className="badge badge-claude">not minted</span>
          </div>
        </div>

        <dl className="kv" style={{ marginTop: 'var(--s-4)' }}>
          <dt>holder</dt>
          <dd className="mono">
            {charter.holderEns ?? (
              <span className="tag tag--warn">ENS name required first</span>
            )}
          </dd>
          <dt>franchise address</dt>
          <dd className="mono">
            {charter.franchise ?? (
              <span className="tag tag--warn">
                nothing minted — no account yet
              </span>
            )}
          </dd>
          <dt>lexicon namespace</dt>
          <dd className="mono">
            {charter.lexiconNamespace ?? (
              <span className="tag tag--muted">no name, no namespace</span>
            )}
          </dd>
          <dt>token-bound account</dt>
          <dd>
            <span className="badge badge-claude">
              ERC-6551 · registry live, nothing minted
            </span>
          </dd>
          <dt>chain</dt>
          <dd className="mono">{charter.chainId}</dd>
        </dl>
      </div>

      {/* ── the rights ────────────────────────────────────────────────────── */}
      <div>
        <div className="page__bar">
          <div className="grow">
            <p className="section-h">what the purchase grants</p>
            <p className="cap">
              Seven rights. Each states where it stands, and names its blocker when
              it is not granted.
            </p>
          </div>
          <span className="chip">
            {charter.rights.filter((r) => r.status === 'on-purchase').length} of{' '}
            {charter.rights.length} granted
          </span>
        </div>

        <div className="tblwrap" style={{ marginTop: 'var(--s-3)' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Right</th>
                <th>What it confers</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {charter.rights.map((r) => (
                <tr key={r.key}>
                  <td>
                    {r.label}
                    <span className="sub mono">{r.key}</span>
                  </td>
                  <td className="cap">
                    {r.grants}
                    {r.blockedBy && (
                      <span className="sub" style={{ color: 'var(--ui-muted)' }}>
                        blocked by: {r.blockedBy}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={TONE[r.status]}>{TONE_WORD[r.status]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── the values score ──────────────────────────────────────────────── */}
      <div className="sheet">
        <p className="section-h">
          community values score <span className="dim">per period</span>
        </p>
        <p className="cap">
          One collective attestation per period, signed by the franchisee through
          the token-bound account. A correction is a new attestation referencing
          the old one — the trail is the point, and no individual is named.
        </p>

        <dl className="kv" style={{ marginTop: 'var(--s-4)' }}>
          <dt>attester</dt>
          <dd className="mono">{charter.values.attester}</dd>
          <dt>subject</dt>
          <dd className="mono">
            {charter.values.subject} · not per member
          </dd>
          <dt>period</dt>
          <dd className="mono">{charter.values.periodDays} days</dd>
          <dt>revocable</dt>
          <dd className="mono">{charter.values.revocable ? 'yes' : 'no'}</dd>
          <dt>supersession</dt>
          <dd className="mono">{charter.values.supersession}</dd>
          <dt>rubric</dt>
          <dd className="mono">{charter.values.rubric}</dd>
          <dt>schema</dt>
          <dd className="mono">{charter.values.schemaKey}</dd>
          <dt>chain</dt>
          <dd className="mono">{charter.values.chainId}</dd>
          <dt>registered</dt>
          <dd>
            <span className="badge badge-claude">no — Phase A5</span>
          </dd>
        </dl>

        <p className="code" style={{ marginTop: 'var(--s-4)' }}>
          {charter.values.schema}
        </p>

        <div className="notice notice--brass" style={{ marginTop: 'var(--s-3)' }}>
          <span className="tick" aria-hidden="true" />
          <span>{charter.values.note}</span>
        </div>
      </div>

      {/* ── the money ─────────────────────────────────────────────────────── */}
      <div className="sheet">
        <p className="section-h">revenue split</p>
        <p className="cap">
          Where subscription and collect revenue lands. Terms are the starting
          shape for a new franchise; they total 100.
        </p>
        <div className="stack" style={{ marginTop: 'var(--s-3)' }}>
          {charter.revenue.terms.map((t) => (
            <div key={t.label}>
              <div className="row row--between">
                <span className="micro micro--ink">{t.label}</span>
                <span className="mono">{t.bps / 100}%</span>
              </div>
              <div
                className="prog-track"
                role="progressbar"
                aria-label={`${t.label} share`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={t.bps / 100}
                style={{ marginTop: 6 }}
              >
                <i className="prog-fill" style={{ width: `${t.bps / 100}%` }} />
              </div>
            </div>
          ))}
        </div>
        <dl className="kv" style={{ marginTop: 'var(--s-4)' }}>
          <dt>distribution</dt>
          <dd className="mono">{charter.revenue.splitMain}</dd>
          <dt>split created</dt>
          <dd>
            <span className="badge badge-claude">no</span>
          </dd>
        </dl>
      </div>

      {/* ── the instruments ───────────────────────────────────────────────── */}
      <div>
        <p className="section-h">
          instruments <span className="dim">creation in scope</span>
        </p>
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Instrument</th>
                <th>Note</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {charter.instruments.map((i) => (
                <tr key={i.key}>
                  <td>{i.label}</td>
                  <td className="cap">{i.note}</td>
                  <td>
                    <span className="badge badge-claude">not wired</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
          Instruments are created <em>under the franchise address</em>, not as
          separate NFT contracts — the token-bound account is already the
          community&apos;s address, and it can own what it creates.
        </p>
      </div>

      {/* ── custody ───────────────────────────────────────────────────────── */}
      <div>
        <p className="section-h">
          custody of the data <span className="dim">not the data model</span>
        </p>
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Graph</th>
                <th>Custody</th>
                <th>Distribution</th>
                <th>Who owns the words</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {CUSTODY_MODES.map((m) => (
                <tr key={m.id}>
                  <td>{m.graph}</td>
                  <td className="mono">{m.custody}</td>
                  <td className="cap">{m.distribution}</td>
                  <td className="cap">{m.lexicon}</td>
                  <td>
                    {m.status === 'new' ? (
                      <span className="badge badge-beta">new mode</span>
                    ) : (
                      <span className="badge badge-admin">{m.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cap" style={{ marginTop: 'var(--s-3)' }}>
          The franchise lexicon is the fourth mode: the holder owns the words, not
          just the rows. Its namespace is{' '}
          <span className="mono">
            {charter.lexiconNamespace ?? '<holder>.lexicon'}
          </span>{' '}
          — currently holding {namespaceTerms.length} term
          {namespaceTerms.length === 1 ? '' : 's'} in the house lexicon.
        </p>
      </div>
      {/* ── the anchor rule ───────────────────────────────────────────────── */}
      <div className="sheet">
        <p className="section-h">
          the anchor rule <span className="dim">one owner per franchise</span>
        </p>
        <p className="cap">{FRANCHISE_ANCHOR.rule}</p>
        <dl className="kv" style={{ marginTop: 'var(--s-4)' }}>
          <dt>anchor</dt>
          <dd className="mono">{FRANCHISE_ANCHOR.anchor}</dd>
          <dt>resolves</dt>
          <dd>
            <span className="badge badge-claude">no</span>
          </dd>
          <dt>blocked by</dt>
          <dd className="cap">{FRANCHISE_ANCHOR.blockedBy}</dd>
        </dl>
      </div>

      {/* ── the matrix ────────────────────────────────────────────────────── */}
      <div>
        <div className="page__bar">
          <div className="grow">
            <p className="section-h">
              sub-apps &amp; feature matrix{' '}
              <span className="dim">every module, and where it stands</span>
            </p>
            <p className="cap">
              The roadmap as data, so no desk has to guess what is real. Status is
              measured against what is in this repo, not against intent.
            </p>
          </div>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {(
              [
                ['live', 'live'],
                ['partial', 'partial'],
                ['scaffolded', 'scaffolded'],
                ['planned', 'planned'],
                ['sequenced-late', 'late'],
                ['blocked', 'blocked'],
              ] as const
            ).map(([k, label]) => (
              <span className="badge badge-claude" key={k}>
                {matrixTotals()[k]} {label}
              </span>
            ))}
          </div>
        </div>

        {MATRIX.map((group) => (
          <div key={group.category} style={{ marginTop: 'var(--s-4)' }}>
            <p className="section-h">{group.category}</p>
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Primitive</th>
                    <th>Execution &amp; function</th>
                    <th>Desk</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {group.modules.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {m.primitive}
                        <span className="sub mono">anchor: {FRANCHISE_ANCHOR.anchor}</span>
                      </td>
                      <td className="cap">
                        {m.fn}
                        {m.inRepo && (
                          <span className="sub" style={{ color: 'var(--ui-muted)' }}>
                            in repo: {m.inRepo}
                          </span>
                        )}
                        {m.blockedBy && (
                          <span className="sub" style={{ color: 'var(--ui-muted)' }}>
                            blocked by: {m.blockedBy}
                          </span>
                        )}
                      </td>
                      <td className="mono">{m.desk}</td>
                      <td>
                        <span className={STATUS_TONE[m.status]}>
                          {m.status.replace('-', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
